"use client";
import {
  Box,
  Flex,
  Grid,
  GridItem,
  Heading,
  Link,
  Text,
} from "@chakra-ui/react";
import { SectionTemplate } from "components/individuals/blocks";
import { ImageFragment } from "lib/gcms";
import Image from "next/image";

export interface UseCaseItemProps {
  title: string;
  description: string;
  icon: ImageFragment & { alt?: string };
  /**
   * Text linking to the Operational Continuity section
   */
  linkedText?: string;
}

export interface UseCaseSectionProps {
  title: string;
  titleColor: string;
  color: string;
  bg: string;
  featuresListItems: UseCaseItemProps[];
}

export const UseCaseSection = ({
  title,
  titleColor,
  color,
  bg,
  featuresListItems,
}: UseCaseSectionProps) => {
  return (
    <SectionTemplate bg={bg}>
      <Heading
        as={"h2"}
        color={titleColor}
        textAlign={"center"}
        fontSize={{
          base: 26,
          md: 34,
          lg: 38,
          xl: 45,
        }}
        fontWeight={"bold"}
      >
        {title}
      </Heading>
      <Grid
        color={color}
        templateColumns={{
          lg: "repeat(2, auto)",
        }}
        pt={{
          base: 8,
          lg: 14,
        }}
        columnGap={20}
        rowGap={10}
        mx={"auto"}
        w={{
          base: "full",
          sm: "70%",
          md: "60%",
          lg: "70%",
        }}
      >
        {featuresListItems?.map((item, idx) => (
          <GridItem key={`use-case-grid-item-${idx}`} textAlign={"center"}>
            <Flex
              flexDir={"column"}
              alignItems={{
                base: "center",
                lg: "flex-start",
              }}
            >
              <Box
                marginLeft={{
                  lg: -5,
                }}
              >
                {item.icon && (
                  <Image
                    {...item.icon}
                    src={item.icon.url}
                    width={item.icon.width / 14}
                    height={item.icon.height / 14}
                    alt={item.icon.alt as string}
                    placeholder={
                      typeof item.icon?.blurDataURL === "string"
                        ? "blur"
                        : undefined
                    }
                    blurDataURL={
                      typeof item.icon?.blurDataURL === "string"
                        ? item.icon?.blurDataURL
                        : undefined
                    }
                  />
                )}
              </Box>
              <Text
                pt={3}
                fontWeight={"bold"}
                fontSize={{
                  base: 16,
                  lg: 17,
                  xl: 18,
                }}
              >
                {item?.title}
              </Text>
              <Text
                textAlign={"left"}
                fontSize={{
                  base: 15,
                  lg: 16,
                  xl: 17,
                }}
                pt={3}
              >
                {item?.description}{" "}
                {item?.linkedText && (
                  <Link
                    href={"#business-continuity-section"}
                    fontWeight={"semibold"}
                    _hover={{
                      textDecoration: "none",
                    }}
                  >
                    {item.linkedText}
                  </Link>
                )}
              </Text>
            </Flex>
          </GridItem>
        ))}
      </Grid>
    </SectionTemplate>
  );
};
